<template>
  <div class="mt-2.5">
    <div v-if="showFileAccess" class="file-access mb-2.5">
      <div class="mb-1 block text-sm font-medium text-gray-700">
        {{ t("common.fileUploadEditor.fileAccess") }}
      </div>
      <div class="mb-2.5 text-xs text-gray-500">
        {{ t("common.fileUploadEditor.fileAccessDescription") }}
      </div>
      <UIRadioGroup
        class="w-full"
        :value="fileAccess"
        name="fileAccess"
        :disabled="fileAccessLocked"
        @update:value="handleFileAccessChange"
      >
        <div class="grid grid-cols-2 gap-2">
          <!--
            Each card needs its own wrapper. naive-ui draws a radio button's
            side borders only on :first-child / :last-child, so two siblings
            render as one segmented control rather than two separate cards.
          -->
          <div>
            <UIRadioGroupItem
              class="w-full"
              value="private"
              :disabled="fileAccessLocked"
              :compact="true"
            >
              <template #content>
                <div class="flex items-center justify-center gap-1 text-sm">
                  {{ t("common.fileUploadEditor.privateFiles") }}
                  <UITooltip trigger="hover" placement="top">
                    <template #trigger>
                      <InfoCircleIcon class="h-4 w-4 shrink-0 text-gray-400" />
                    </template>
                    {{ t("common.fileUploadEditor.privateFilesTooltip") }}
                  </UITooltip>
                </div>
              </template>
            </UIRadioGroupItem>
          </div>
          <div>
            <UIRadioGroupItem
              class="w-full"
              value="public"
              :disabled="fileAccessLocked"
              :compact="true"
            >
              <template #content>
                <div class="flex items-center justify-center gap-1 text-sm">
                  {{ t("common.fileUploadEditor.publicFiles") }}
                  <UITooltip trigger="hover" placement="top">
                    <template #trigger>
                      <InfoCircleIcon class="h-4 w-4 shrink-0 text-gray-400" />
                    </template>
                    {{ t("common.fileUploadEditor.publicFilesTooltip") }}
                  </UITooltip>
                </div>
              </template>
            </UIRadioGroupItem>
          </div>
        </div>
      </UIRadioGroup>
      <div v-if="fileAccessLocked" class="mt-2 text-xs text-gray-500">
        {{ t("common.fileUploadEditor.fileAccessLocked") }}
      </div>
    </div>
    <div class="mb-2.5 flex items-center text-sm font-medium text-gray-700">
      {{ t("common.fileUploadEditor.fileTypes") }}
    </div>
    <UICheckboxGroup
      :value="options"
      class="flex items-center space-x-2 text-sm"
      :disabled="false"
      @update:value="handleUpdate"
    >
      <UICheckbox id="all" class="w-1/4" value="all" :label="t('common.fileUploadEditor.all')" />
      <UICheckbox id="pdf" class="w-1/4" value=".pdf" :label="t('common.fileUploadEditor.pdf')" />
      <UICheckbox
        id="doc"
        class="w-1/2"
        value=".doc"
        :label="t('common.fileUploadEditor.docxDoc')"
      />
      <UICheckbox id="png" class="w-1/4" value=".png" :label="t('common.fileUploadEditor.png')" />
      <UICheckbox id="gif" class="w-1/4" value=".gif" :label="t('common.fileUploadEditor.gif')" />
      <UICheckbox
        id="jpg"
        class="w-1/2"
        value=".jpg"
        :label="t('common.fileUploadEditor.jpgJpeg')"
      />
      <UICheckbox
        id="xls"
        class="w-1/2"
        value=".xls"
        :label="t('common.fileUploadEditor.xlsCsv')"
      />
    </UICheckboxGroup>
  </div>
</template>

<script setup lang="ts">
import { InfoCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UICheckbox,
  UICheckboxGroup,
  UIRadioGroup,
  UIRadioGroupItem,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { computed, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  options: {
    type: Array as PropType<string[]>,
    default: null,
  },
  /** Uploads to this field are access-controlled. Defaults to public. */
  isPrivate: {
    type: Boolean,
    default: false,
  },
  /**
   * File access cannot be changed once the custom field exists, so the control
   * is only editable while the field is still being created.
   */
  fileAccessLocked: {
    type: Boolean,
    default: true,
  },
  /**
   * Private uploads are only supported for contact fields, so callers opt in.
   * Defaults to hidden: showing it for an unsupported model would create a
   * field that silently is not private.
   */
  showFileAccess: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:options", "update:isPrivate"]);

const FILE_ACCESS = { private: "private", public: "public" } as const;

const fileAccess = computed(() =>
  props.isPrivate ? FILE_ACCESS.private : FILE_ACCESS.public
);

const handleFileAccessChange = (value: string) => {
  if (props.fileAccessLocked) return;
  emit("update:isPrivate", value === FILE_ACCESS.private);
};

const options = ref(props.options?.length ? props.options : ["all"]);

const handleUpdate = (newValue) => {
  if (newValue[newValue?.length - 1] === "all") {
    options.value = ["all"];
  } else if (newValue.includes("all") && newValue?.length > 1) {
    options.value = newValue.filter((option) => option !== "all");
  } else {
    const acceptedFormat = [...newValue];

    if (acceptedFormat.includes(".jpg")) {
      !acceptedFormat.includes(".jpeg") ? acceptedFormat.push(".jpeg") : null;
    } else {
      acceptedFormat.includes(".jpeg")
        ? acceptedFormat.splice(acceptedFormat.indexOf(".jpeg"), 1)
        : null;
    }

    if (acceptedFormat.includes(".doc")) {
      !acceptedFormat.includes(".docx") ? acceptedFormat.push(".docx") : null;
    } else {
      acceptedFormat.includes(".docx")
        ? acceptedFormat.splice(acceptedFormat.indexOf(".docx"), 1)
        : null;
    }

    if (acceptedFormat.includes(".xls")) {
      !acceptedFormat.includes(".csv") ? acceptedFormat.push(".csv") : null;
      !acceptedFormat.includes(".xlsx") ? acceptedFormat.push(".xlsx") : null;
    } else {
      acceptedFormat.includes(".csv")
        ? acceptedFormat.splice(acceptedFormat.indexOf(".csv"), 1)
        : null;
      acceptedFormat.includes(".xlsx")
        ? acceptedFormat.splice(acceptedFormat.indexOf(".xlsx"), 1)
        : null;
    }

    options.value = acceptedFormat;
  }

  emit("update:options", options.value);
};
</script>

<style scoped>
/*
 * The compact radio card hides its radio dot but keeps the 16px gutters that
 * separated the dot from the label. In a half-width sidebar column that dead
 * space is what pushes "Private files" onto a second line, so drop it here.
 * The selector mirrors ghl-ui's own three-class chain because a shorter one
 * loses to it on specificity.
 */
.file-access
  :deep(
    .hl-radio-group-item.n-radio-button
      .hl-radio-group-item-container
      .hl-radio-group-item-content
  ) {
  padding-left: 0;
  padding-right: 0;
}
</style>
